import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TenantSummary, page } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Badge, Button, Card, ConfirmButton, EmptyRow, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { BASE, MembershipArray, SrMembership, keys, statusTone } from './api';
import type { SrBuilding } from './api';

const TenantPage = page(TenantSummary);

/** Invite an existing customer, then follow the invitation: invited, active, paused, leaving, declined, withdrawn. */
export function InvitationsTab({ building, canManage }: { building: SrBuilding; canManage: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [tenantId, setTenantId] = useState('');
  const memberships = useQuery({ queryKey: keys.memberships(building.id), queryFn: () => api.get(`${BASE}/${building.id}/memberships`, { schema: MembershipArray }) });
  const tenants = useQuery({
    queryKey: keys.tenants(search),
    queryFn: () => api.get('/platform/tenants', { query: { pageSize: 50, search, sort: 'name', status: 'active' }, schema: TenantPage }),
    enabled: canManage,
  });
  // A customer with a live invitation or membership can't be invited again (the server refuses with sr_already_member too).
  const busy = new Set((memberships.data ?? []).filter((m) => !['left', 'declined', 'withdrawn'].includes(m.status)).map((m) => m.tenantId));
  const options = (tenants.data?.items ?? []).filter((x) => !busy.has(x.id));

  const refresh = () => qc.invalidateQueries({ queryKey: ['shared-reception', 'platform'] });
  const invite = useMutation({
    mutationFn: () => api.post(`${BASE}/${building.id}/invitations`, { body: { tenantId }, schema: SrMembership }),
    onSuccess: async () => {
      setTenantId('');
      await refresh();
      toast(t('srp.inv.sent'));
    },
  });
  const withdraw = useMutation({
    mutationFn: (membershipId: string) => api.delete(`${BASE}/${building.id}/invitations/${membershipId}`),
    onSuccess: async () => {
      await refresh();
      toast(t('srp.inv.withdrawn'));
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (tenantId) invite.mutate();
  };
  const zone = building.timeZone;
  return (
    <div className="space-y-6">
      {canManage && building.status === 'active' ? (
        <Card title={t('srp.inv.invite')}>
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-3 sm:items-end">
            <TextField label={t('srp.inv.search')} hint={t('srp.inv.searchHint')} type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
            <SelectField label={t('srp.inv.customer')} value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
              <option value="">{t('srp.inv.choose')}</option>
              {options.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </SelectField>
            <div>
              <Button type="submit" variant="primary" disabled={!tenantId || invite.isPending}>
                {t('srp.inv.send')}
              </Button>
            </div>
          </form>
          <ErrorLine error={invite.error} />
        </Card>
      ) : null}
      <ErrorLine error={withdraw.error} />
      {memberships.isPending ? (
        <Spinner />
      ) : memberships.isError ? (
        <ErrorPanel error={memberships.error} onRetry={() => void memberships.refetch()} />
      ) : (
        <TableWrap caption={t('srp.inv.table')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>{t('srp.inv.col.customer')}</Th>
              <Th>{t('srp.col.status')}</Th>
              <Th>{t('srp.inv.col.site')}</Th>
              <Th>{t('srp.inv.col.invited')}</Th>
              <Th>{t('srp.inv.col.answered')}</Th>
              {canManage ? <Th>{t('srp.col.actions')}</Th> : null}
            </tr>
          </thead>
          <tbody>
            {memberships.data.length === 0 ? <EmptyRow cols={canManage ? 6 : 5} /> : null}
            {memberships.data.map((m) => {
              const name = m.tenantName ?? m.displayName ?? m.tenantId;
              return (
                <tr key={m.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium">{name}</td>
                  <td className="px-4 py-3">
                    <Badge tone={statusTone(m.status)}>{t(`sr.status.${m.status}`)}</Badge>
                    {m.status === 'leaving' && m.leavingUntil ? <div className="mt-1 text-xs text-slate-700">{t('srp.inv.leavingUntil', { date: formatDateTime(m.leavingUntil, zone) })}</div> : null}
                  </td>
                  <td className="px-4 py-3">{m.siteName ?? ''}</td>
                  <td className="px-4 py-3">{formatDateTime(m.invitedAt, zone)}</td>
                  <td className="px-4 py-3">{formatDateTime(m.acceptedAt, zone)}</td>
                  {canManage ? (
                    <td className="px-4 py-3">
                      {m.status === 'invited' ? (
                        <ConfirmButton label={t('srp.inv.withdraw')} onConfirm={() => withdraw.mutate(m.id)} pending={withdraw.isPending} />
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </TableWrap>
      )}
    </div>
  );
}
